import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import SearchInput from '../../../../../components/common-inputs/SearchInput';
import TooltipButton from '../../../../../components/tooltip-button/TooltipButton';
import { BUTTON_STYLES } from '../../../../../components/common-button/CommonButton';
import ConfirmationModal from '../../../../../components/confirmation-modal/ConfirmationModal';
import useCommonModal from '../../../../../hooks/useCommonModal';
import Icon from '../../../../../icons/Icon';
import { ICONS } from '../../../../../icons';
import { useProject } from '../../../../../context/ProjectContext';
import { ACTIV8_MESSAGE_GENERATE_TABS } from '../../../../../constants';
import CreateMessageModal from '../../../../../scout-new/components/create-message-modal/CreateMessageModal';

import scss from './MessagesFilters.module.scss';

function MessagesFilters({ onClearAll, query, onChangeSearch }) {
  const { actions } = useProject();

  const { t } = useTranslation('activ8');

  const {
    isOpen: isClearAllModalOpen,
    handleOpenModal: handleOpenClearAllModal,
    handleCloseModal: handleCloseClearAllModal,
  } = useCommonModal(false);
  const {
    isOpen: isShowCreateMessageModal,
    handleOpenModal: handleOpenCreateMessageModal,
    handleCloseModal: handleCloseCreateMessageModal,
  } = useCommonModal(false);

  const isGenerating = useSelector((state) => state.activ8Messages.isGenerating);
  const isFormLoading = useSelector((state) => state.activ8Messages.isFormLoading);
  const isResetting = useSelector((state) => state.activ8Messages.isResetting);
  const isCreating = useSelector((state) => state.activ8Messages.isCreating);

  const generateTab = useSelector((state) => state.activ8Messages.generateTab);

  const disableButtons = isFormLoading || isGenerating || isResetting || isCreating;

  const handleClearAll = () => {
    onClearAll();
  };

  const handleCreateMessage = async ({ projectId, messages }) => {
    await actions('createMessage', { projectId, messages });
  };

  const mappedConfirmationModalLabels = {
    [ACTIV8_MESSAGE_GENERATE_TABS.GENERATED]: t('allUnsavedMessagesWillBeDeleted'),
    [ACTIV8_MESSAGE_GENERATE_TABS.SAVED]: t('areYouSureYouWantToDeleteMessageHistory'),
  };

  return (
    <div className={scss.filtersWrapper}>
      <div className={scss.leftSection}>
        {generateTab === ACTIV8_MESSAGE_GENERATE_TABS.SAVED ? (
          <TooltipButton
            onClick={handleOpenCreateMessageModal}
            styleColor={BUTTON_STYLES.PRIMARY}
            type='button'
            maxWidth={160}
            isLoading={isCreating}
            disabled={disableButtons}
            className={scss.generateButton}>
            {t('customMessage')}
          </TooltipButton>
        ) : null}
      </div>

      <div className={scss.rightSection}>
        <SearchInput
          value={query}
          onChange={onChangeSearch}
          size='sm'
          placeholder={t('searchByWord')}
          maxWidth={280}
          disabled={disableButtons}
          extraWrapperClass={scss.inputMedium}
        />

        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          maxWidth={130}
          onClick={handleOpenClearAllModal}
          disabled={disableButtons}
          tooltip={isGenerating && t('messagesGenerating')}
          isStrokeIcon>
          {t('clearAll')}
          <Icon icon={ICONS.Cross} disableCommonColors className={scss.crossIcon} />
        </TooltipButton>
      </div>

      <ConfirmationModal
        isOpen={isClearAllModalOpen}
        onClose={handleCloseClearAllModal}
        onCancel={handleCloseClearAllModal}
        onConfirm={handleClearAll}
        label={mappedConfirmationModalLabels[generateTab]}
        subLabel={t('thisCanNotBeReversed')}
      />

      <CreateMessageModal
        isOpen={isShowCreateMessageModal}
        onClose={handleCloseCreateMessageModal}
        onCreateMessage={handleCreateMessage}
      />
    </div>
  );
}

export default MessagesFilters;
